import * as API from '@/API';
import { type MultiSelectOption } from '@/Components/MultiSelect/MultiSelect';
import type TextBlockModel from '@/Models/TextBlock/TextBlockModel';
import { type TextBlockInsertable } from '@/Models/TextBlock/TextBlockModel';
import type ValueIDMap from '@/Models/ValueIDMap';
import type FormValidation from '@/Pages/TextBlockDetail/Models/FormValidation';
import type FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { makeAutoObservable } from 'mobx';

interface StateData {
    dialogIsOpen: boolean;
    isLoaded: boolean;
    isButtonsDisabled: boolean;
    isFormValid: FormValidation;
    validationErrorMessage: string;
    textBlock: TextBlockModel;
    isEdit: boolean;
    procedures: MultiSelectOption[];
    modalities: MultiSelectOption[];
    headerStyles: string[];
}

export default class TextBlockDetailStore {
    state: StateData = {
        dialogIsOpen: false,
        isLoaded: false,
        isButtonsDisabled: false,
        isFormValid: {
            Name: true,
            SearchTerm: true,
            Content: true,
        },
        validationErrorMessage: '',
        textBlock: {
            ID: -1,
            Name: '',
            Pronunciation: '',
            Content: '',
            UseInReporting: false,
            UseInG2Speech: false,
            AutoInsert: false,
            LastModificationDateTime: '',
            ModifiedByName: '',
            SelectedRadiologists: [],
            AvailableRadiologists: [],
            SearchTerm: '',
            Language: '',
            ApplicableFor: 0,
            Procedures: [],
            Modalities: [],
        },
        isEdit: false,
        procedures: [],
        modalities: [],
        headerStyles: [],
    };

    private readonly fcStore: FilingCabinetStore<StateData>;
    private readonly textBlockID: number;
    private readonly t: (str: string) => string;
    showErrorDialog: boolean;
    errorDialogMessage: string;

    constructor(fcStore: FilingCabinetStore<StateData>, useTranslation: (str: string) => string, isEdit: boolean, textBlockID: number) {
        this.fcStore = fcStore;
        this.t = useTranslation;
        this.state.isEdit = isEdit;
        this.textBlockID = textBlockID;
        this.showErrorDialog = false;
        this.errorDialogMessage = '';

        makeAutoObservable(this);

        this.fcStore.initializeState(this.initOld, this.initNew);
    }

    private readonly initOld = (state: StateData): void => {
        this.setTitle(state.textBlock?.Name ?? '');

        this.setState({
            dialogIsOpen: state.dialogIsOpen,
            isLoaded: state.isLoaded,
            isButtonsDisabled: state.isButtonsDisabled,
            isFormValid: state.isFormValid,
            validationErrorMessage: state.validationErrorMessage,
            textBlock: state.textBlock,
            isEdit: state.isEdit,
            procedures: state.procedures,
            modalities: state.modalities,
        });
    };

    private readonly initNew = (): void => {
        void Promise.all([API.setup.textBlocks.textBlockDetail(), this.getTextBlockDetail()])
            .then(([hydrateData]) => {
                this.setProcedures(hydrateData.Procedures);
                this.setModalities(hydrateData.Modalities);
                this.fcStore.updateState(this.state);
            });
    };

    private readonly setTitle = (title: string): void => {
        if (this.state.isEdit) {
            this.fcStore.updateTitle(`${this.t('DetailTextBlock')} - ${title}`);
        }
        else {
            this.fcStore.updateTitle(this.t('AddNewTextBlock'));
        }
    };

    setState = (newState: any): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    setIsLoaded = (newState: boolean): void => {
        this.state.isLoaded = newState;
    };

    setTextBlock = (textBlock: TextBlockModel): void => {
        this.state.textBlock = textBlock;
        this.fcStore.updateState(this.state);
    };

    setIsFormValid = (val: FormValidation): void => {
        this.state.isFormValid = val;
        this.fcStore.updateState(this.state);
    };

    setValidationErrorMessage = (val: string): void => {
        this.state.validationErrorMessage = val;
        this.fcStore.updateState(this.state);
    };

    setIsButtonsDisabled = (val: boolean): void => {
        this.state.isButtonsDisabled = val;
        this.fcStore.updateState(this.state);
    };

    setDialogIsOpen = (val: boolean): void => {
        this.state.dialogIsOpen = val;
        this.fcStore.updateState(this.state);
    };

    setChosenProcedures = (val: MultiSelectOption[]): void => {
        this.state.textBlock.Procedures = val.map((x) => x as ValueIDMap);
        this.fcStore.updateState(this.state);
    };

    setChosenModalities = (val: MultiSelectOption[]): void => {
        this.state.textBlock.Modalities = val.map((x) => x as ValueIDMap);
        this.fcStore.updateState(this.state);
    };

    setContent = (val: string): void => {
        this.setTextBlock({
            ...this.state.textBlock,
            Content: val,
        });
    };

    setApplicableFor = (val: TextBlockInsertable): void => {
        this.setTextBlock({
            ...this.state.textBlock,
            ApplicableFor: val,
        });
    };

    setLanguage = (val: string): void => {
        this.setTextBlock({
            ...this.state.textBlock,
            Language: val,
        });
    };

    setSelectedRadiologists = (val: number[]): void => {
        this.setTextBlock({
            ...this.state.textBlock,
            SelectedRadiologists: val,
        });
    };

    private readonly setModalities = (val: MultiSelectOption[]): void => {
        this.state.modalities = val;
    };

    private readonly setProcedures = (val: MultiSelectOption[]): void => {
        this.state.procedures = val;
    };

    private readonly getTextBlockDetail = async (): Promise<void> => {
        this.setIsLoaded(false);
        await API.textblocks.getDetailTextBlock(this.textBlockID).then((data) => {
            if (!this.state.isEdit) {
                data.Language = 'NL';
            }

            this.setTitle(data.Name ?? '');
            this.setTextBlock(data);
            this.setIsLoaded(true);
        }).catch((err) => {
            console.error('Error fetching Text block:', err);
        });
    };

    validateFields = (): boolean => {
        this.setIsFormValid({
            ...this.state.isFormValid,
            Name: true,
            SearchTerm: true,
            Content: true,
        });

        if (this.state.textBlock.Name == null || this.state.textBlock.Name.trim() === '') {
            this.setIsFormValid({ ...this.state.isFormValid, Name: false });
            this.setValidationErrorMessage(this.t('Required'));
            return false;
        }
        if (this.state.textBlock.SearchTerm == null || this.state.textBlock.SearchTerm.trim() === '') {
            this.setIsFormValid({ ...this.state.isFormValid, SearchTerm: false });
            this.setValidationErrorMessage(this.t('Required'));
            return false;
        }
        if (this.state.textBlock.Content == null || this.state.textBlock.Content.trim() === '') {
            this.setIsFormValid({ ...this.state.isFormValid, Content: false });
            this.setValidationErrorMessage(this.t('Required'));
            return false;
        }

        return true;
    };

    checkAll = (): void => {
        this.setTextBlock({
            ...this.state.textBlock,
            SelectedRadiologists: this.state.textBlock.AvailableRadiologists.map((radiologist) => radiologist.ID),
        });
    };

    uncheckAll = (): void => {
        this.setTextBlock({
            ...this.state.textBlock,
            SelectedRadiologists: [],
        });
    };

    setShowDialogError = (val: boolean): void => {
        this.showErrorDialog = val;
    };

    setDialogErrorMessage = (val: string): void => {
        this.errorDialogMessage = val;
    };

    setHeaderStyles = (val: string[]): void => {
        this.state.headerStyles = val;
    };
};
